import type { GenderId } from './gender';
import type { HasChildrenId } from './hasChildren';
import type { MarriageStatusId } from './marriageStatus';
import type { PrefectureId } from './prefecture';
import type { Birthdate } from '@/packages/nurse-senka-frontend-sdk';

type UpdateBasicInfoColumns = {
  name: boolean;
  preferredUsername: boolean;
  gender: boolean;
  phoneNumber: boolean;
  address: boolean;
  familyStructure: boolean;
  birthdate: boolean;
  profile: boolean;
  selfIntroduction: boolean;
};

const updateBasicInfoColumns: UpdateBasicInfoColumns = {
  name: false,
  preferredUsername: false,
  gender: false,
  phoneNumber: false,
  address: false,
  familyStructure: false,
  birthdate: true,
  profile: false,
  selfIntroduction: false,
};

export const createInitUpdateBasicInfoColumns = (): UpdateBasicInfoColumns =>
  updateBasicInfoColumns;

export type NameUpdaterRequest = {
  givenName: string;
  familyName: string;
  givenNameKana: string;
  familyNameKana: string;
};

export type PreferredUsernameUpdaterRequest = {
  preferredUsername: string;
};

export type GenderUpdaterRequest = {
  gender: GenderId;
};

export type PhoneNumberUpdaterRequest = {
  phoneNumber: string;
};

export type AddressUpdaterRequest = {
  postalCode: string;
  prefectureId: PrefectureId;
  locality: string;
  streetAddress: string;
  building?: string;
};

export type FamilyStructureUpdaterRequest = {
  marriageStatus: MarriageStatusId;
  hasChildren: HasChildrenId;
};

export type BirthdateUpdaterRequest = {
  birthdate: Birthdate;
};

export type ProfileUpdaterRequest = {
  profileImage?: string;
  profileImageExtension?: string;
  selfIntroduction?: string;
};

export const createProfileUpdaterRequest = (
  params: ProfileUpdaterRequest,
): ProfileUpdaterRequest => {
  const result: ProfileUpdaterRequest = {};

  if (params.profileImage && params.profileImageExtension) {
    // eslint-disable-next-line no-unused-expressions
    result.profileImage = params.profileImage;
    result.profileImageExtension = params.profileImageExtension;
  }

  if (params.selfIntroduction) {
    result.selfIntroduction = params.selfIntroduction;
  }

  return result;
};

export type BasicInfoUpdaters = {
  name: (request: NameUpdaterRequest) => Promise<void>;
  preferredUsername: (
    request: PreferredUsernameUpdaterRequest,
  ) => Promise<void>;
  gender: (request: GenderUpdaterRequest) => Promise<void>;
  phoneNumber: (request: PhoneNumberUpdaterRequest) => Promise<void>;
  address: (request: AddressUpdaterRequest) => Promise<void>;
  familyStructure: (request: FamilyStructureUpdaterRequest) => Promise<void>;
  birthdate: (request: BirthdateUpdaterRequest) => Promise<void>;
  profile: (request: ProfileUpdaterRequest) => Promise<void>;
};

type UpdateErrorName =
  | 'PreferredUsernameIncludedForbiddenWordsError'
  | 'PreferredUsernameValidationError';

export const createUpdateFailedMessage = (error: unknown) => {
  const defaultMessage =
    '予期せぬエラーが発生しました。お手数ですがしばらく時間が経ってからお試し下さい。';

  if (error instanceof Error) {
    const errorName = error.name as UpdateErrorName;

    switch (errorName) {
      case 'PreferredUsernameIncludedForbiddenWordsError':
        return 'ニックネームに禁止文字（未設定など）が含まれています。';
      case 'PreferredUsernameValidationError':
        return 'ニックネームに記号なしで1文字以上50文字以上で入力して下さい。';
      default:
        return defaultMessage;
    }
  }

  return defaultMessage;
};
